import React, { lazy, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { reactLocalStorage } from "reactjs-localstorage";

import MainLayout from "layout/MainLayout";
import Loadable from "ui-component/Loadable";

import Orders from "views/pages/orders/orders";
import AddOns from "views/pages/addon/addons";
import AddAddOn from "views/pages/addon/addAddon";
import Categories from "views/pages/settings/categories/categories";
import OrderDetail from "views/pages/orders/orderDetail";
import CreateOrder from "views/pages/orders/createOrder";
import Payments from "views/pages/payment/payments";
import Terms from "views/pages/settings/terms/terms";
import AddTerm from "views/pages/settings/terms/addTerm";
import EditProfile from "views/pages/settingProfile/EditProfile";
import Vouchers from "views/pages/vouchers/vouchers";
import AddVoucher from "views/pages/vouchers/addVoucher";
import PackageList from "views/pages/package/packageList";
import AddPackage from "views/pages/package/addPackage";
import Coupon from "views/pages/coupons/coupons";
import AddCoupon from "views/pages/coupons/addCoupon";
import Classes from "views/pages/classes/classes";
import AddClass from "views/pages/classes/addClass";
import Merchants from "views/pages/merchant/merchants";
import Merchant from "views/pages/merchant/merchant";
import AddMerchantAdmin from "views/pages/merchant/component/admin/addMerchantAdmin";

import { getUrlCourtCode } from "helper/Court";
import { adminUrl } from "config/config";
import { isMerchant, isSTCAdmin } from "helper/Admin";
import Players from "views/pages/players/players";
import PlayerDetail from "views/pages/players/playerDetail";

// dashboard routing
const DashboardDefault = Loadable(lazy(() => import("views/pages/dashboard/Dashboard")));

// ============================================================================
// 1) Permissions (Context + Provider) - in ONE FILE
// ============================================================================

const PermissionContext = createContext(null);

async function fetchPermissionsFromAPI({ signal } = {}) {
    if (isSTCAdmin()) return []; // ✅ merchant skip
    const token =
        reactLocalStorage.get("user_token") ||
        localStorage.getItem("user_token"); // ✅ fallback

    console.log("[perm] token:", token); // ✅ debug

    let userId = localStorage.getItem("user_info") ? JSON.parse(localStorage.getItem('user_info')) : null

    if (!token) return [];

    const res = await fetch(`${adminUrl}/admin/${userId._id}`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
        },
        signal,
    });

    if (res.status == "405") {
        localStorage.clear()
        reactLocalStorage.clear()
        window.location.href = "/login"
    }

    console.log("[perm] /profile status:", res.status); // ✅ debug

    const json = await res.json();
    console.log("[perm] profile response:", json); // ✅ debug

    localStorage.setItem("allowed_menu", JSON.stringify(json?.data?.allowedMenu || json?.data?.merchantInfo?.setting?.allowedMenu || []));
    window.dispatchEvent(new Event("allowed_menu_updated"));

    return json?.data?.allowedMenu || json?.data?.merchantInfo?.setting?.allowedMenu || [];
}

const CACHE_KEY = "allowed_menu_cache_v1";

function PermissionProvider({ children }) {
    const location = useLocation();

    const [permissions, setPermissions] = useState(() => {
        try {
            const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || "[]");
            return Array.isArray(cached) ? cached : [];
        } catch {
            return [];
        }
    });

    const [loadingPerms, setLoadingPerms] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);

    // ✅ add this
    const [menuVersion, setMenuVersion] = useState(0);

    const loadPermissions = useCallback(async () => {
        setLoadingPerms(true);
        try {
            const perms = await fetchPermissionsFromAPI();
            const safe = Array.isArray(perms) ? perms : [];
            setPermissions(safe);
            localStorage.setItem(CACHE_KEY, JSON.stringify(safe));

            // ✅ bump version so sidebar rerenders (even if array "looks" same)
            setMenuVersion((v) => v + 1);
        } finally {
            setLoadingPerms(false);
            setHasLoaded(true);
        }
    }, []);

    useEffect(() => {
        const isLoggedIn = Boolean(reactLocalStorage.get("user_token"));
        if (!isLoggedIn) {
            setPermissions([]);
            localStorage.removeItem(CACHE_KEY);
            setHasLoaded(false);
            setMenuVersion((v) => v + 1);
            return;
        }
        loadPermissions();
    }, [location.pathname, loadPermissions]);

    // ✅ also react to manual allowed_menu_updated events (same tab)
    useEffect(() => {
        const onUpdate = () => setMenuVersion((v) => v + 1);
        window.addEventListener("allowed_menu_updated", onUpdate);
        return () => window.removeEventListener("allowed_menu_updated", onUpdate);
    }, []);

    const permissionSet = useMemo(() => new Set(permissions), [permissions]);

    const value = useMemo(
        () => ({
            permissions,
            permissionSet,
            loadingPerms,
            hasLoaded,
            reloadPermissions: loadPermissions,

            // ✅ expose version
            menuVersion,
        }),
        [permissions, permissionSet, loadingPerms, hasLoaded, loadPermissions, menuVersion]
    );

    return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

function usePermissions() {
    const ctx = useContext(PermissionContext);
    if (!ctx) throw new Error("usePermissions must be used within PermissionProvider");
    return ctx;
}

const court = getUrlCourtCode();

// ============================================================================
// 2) PermissionGuard - block access globally
// ============================================================================

function PermissionGuard({ permission, children }) {
    const { permissionSet, loadingPerms, hasLoaded } = usePermissions();

    // ✅ first time boot: don't decide yet
    if (!hasLoaded || loadingPerms) {
        return <div style={{ padding: 16 }}>Loading access...</div>;
    }

    if (!permission) return children;

    if (!permissionSet.has(permission)) {
        return <Navigate to={`/${court}/dashboard`} replace />;
    }

    return children;
}

const withPerm = (permission, element) => {
    // ✅ merchant: don't guard using permissions context
    if (isSTCAdmin()) return element;

    // ✅ admin: guard as usual
    return <PermissionGuard permission={permission}>{element}</PermissionGuard>;
};

// ============================================================================
// 3) Root wrapper (auth + provider + layout)
// ============================================================================

function MerchantRoot() {
    const isLoggedIn = Boolean(reactLocalStorage.get("user_token"));
    if (!isLoggedIn) return <Navigate to="/login" replace />;
    if (isSTCAdmin()) {
        return <MainLayout />;
    }

    return (
        <PermissionProvider>
            <MainLayout />
        </PermissionProvider>
    );
}

// ============================================================================
// 4) Routes (no static filter). Always register routes, gate them.
// ============================================================================

const MerchantRoutes = {
    path: "/",
    element: <MerchantRoot />,
    children: [
        // Dashboard
        { path: "/", element: withPerm("dashboard", <DashboardDefault />) },
        { path: `/${court}/dashboard`, element: withPerm("dashboard", <DashboardDefault />) },

        // Orders
        { path: `/${court}/users`, element: withPerm("user", <Players />) },
        { path: `/${court}/users/detail/:playerId`, element: withPerm("user", <PlayerDetail />) },

        // Orders
        { path: `/${court}/orders`, element: withPerm("order", <Orders />) },
        { path: `/${court}/orders/create-order`, element: withPerm("order", <CreateOrder />) },
        { path: `/${court}/orders/:id`, element: withPerm("order", <OrderDetail />) },

        // Payments
        { path: `/${court}/payments`, element: withPerm("payment", <Payments />) },

        // Add-ons
        { path: `/${court}/addon`, element: withPerm("add_on", <AddOns />) },
        { path: `/${court}/addon/add`, element: withPerm("add_on", <AddAddOn />) },
        { path: `/${court}/addon/edit/:addonId`, element: withPerm("add_on", <AddAddOn />) },

        // Always available for logged-in (no permission required)
        { path: `/${court}/categories`, element: <Categories /> },
        { path: `/${court}/term-and-conditions`, element: <Terms /> },
        { path: `/${court}/term-and-condition/add`, element: <AddTerm /> },
        { path: `/${court}/term-and-condition/edit/:Id`, element: <AddTerm /> },
        { path: `/${court}/setting-profile/:adminId`, element: <EditProfile /> },

        // Marketing
        { path: `/${court}/vouchers`, element: withPerm("voucher", <Vouchers />) },
        { path: `/${court}/vouchers/add`, element: withPerm("voucher", <AddVoucher />) },

        { path: `/${court}/packages`, element: withPerm("value_pack", <PackageList />) },
        { path: `/${court}/packages/add`, element: withPerm("value_pack", <AddPackage />) },
        { path: `/${court}/packages/edit/:packageId`, element: withPerm("value_pack", <AddPackage />) },

        { path: `/${court}/coupons`, element: withPerm("promo_code", <Coupon />) },
        { path: `/${court}/coupons/add`, element: withPerm("promo_code", <AddCoupon />) },
        { path: `/${court}/coupons/edit/:couponId`, element: withPerm("promo_code", <AddCoupon />) },

        // Classes
        { path: `/${court}/classes`, element: withPerm("classes", <Classes />) },
        { path: `/${court}/classes/add`, element: withPerm("classes", <AddClass />) },
        { path: `/${court}/classes/edit/:classId`, element: withPerm("classes", <AddClass />) },

        // Merchant
        { path: `/${court}/merchants`, element: <Merchants /> },
        { path: `/${court}/merchants/add`, element: <Merchant /> },
        { path: `/${court}/merchants/edit/:id`, element: <Merchant /> },

        // Admin
        // { path: `/${court}/admins/add`, element: <AddAdmin /> },
        // { path: `/${court}/admins/edit/:adminId`, element: <AddAdmin /> },
        { path: `/${court}/merchants/admins/add`, element: withPerm("settings_admin", <AddMerchantAdmin />) },
        { path: `/${court}/merchants/admins/edit/:adminId`, element: withPerm("settings_admin", <AddMerchantAdmin />) },

        // // No access
        // {
        //     path: "/no-access",
        //     element: (
        //         <div style={{ padding: 24 }}>
        //             <h2>No Access</h2>
        //             <p>You don’t have permission to access this page.</p>
        //         </div>
        //     )
        // },

        // fallback
        { path: "*", element: <Navigate to="/" replace /> }
    ]
};

export default MerchantRoutes;